import type { ConductionEdge, HeatPumpEdge } from "./networks";
import type { ItemDef, ModuleDef, Ship, ShipModule } from "./types";

export const STEEL_J_PER_KG_K = 500;
export const ROCK_J_PER_KG_K  = 800;
export const ICE_J_PER_KG_K   = 2100;
export const SPECIFIC_HEAT_J_PER_KG_K = STEEL_J_PER_KG_K;

export const AMBIENT_K = 290;

/** Draper point; mirrors heat-glow.wgsl GLOW_MIN_K. */
export const GLOW_TEMP_K = 800;

const DISSIPATION_REF_DT_K = 500;
const T_REF_K = AMBIENT_K + DISSIPATION_REF_DT_K;
const AMBIENT_K4 = AMBIENT_K ** 4;
export const T4_REF_RANGE_K4 = T_REF_K ** 4 - AMBIENT_K4;

const RADIATE_W_PER_CELL = 200;
const RADIATE_W_PER_KG = 0.4;

export function moduleRadiateCoefW(def: ModuleDef): number {
  const cells = def.cells.w * def.cells.h;
  const base = RADIATE_W_PER_CELL * cells + RADIATE_W_PER_KG * Math.max(1, def.stats.mass);
  return base * def.stats.surfaceAreaMult * def.stats.thermalEmissivity;
}

export function itemRadiateCoefW(def: ItemDef): number {
  const base = RADIATE_W_PER_KG * Math.max(1, def.stats.mass);
  return base * def.stats.surfaceAreaMult * def.stats.thermalEmissivity;
}

/** Reactor: P_out·(1−η)/η. Others: input·(1−η). Thrusters: 0 (per-port heatW). */
export function moduleActiveWasteHeatW(def: ModuleDef): number {
  const eff = def.stats.energyEfficiency;
  if (def.category === "reactor") {
    if (def.stats.powerOutW <= 0 || eff <= 0 || eff >= 1) return 0;
    return def.stats.powerOutW * (1 - eff) / eff;
  }
  if (eff >= 1) return 0;
  return (def.stats.powerIdleW + def.stats.powerActiveW) * (1 - eff);
}

export function radiativeCool(
  tempK: number,
  heatCapJperK: number,
  radiateCoefW: number,
  dt: number,
): number {
  if (tempK <= AMBIENT_K || radiateCoefW <= 0 || heatCapJperK <= 0) return tempK;
  const t2 = tempK * tempK;
  const factor = (t2 * t2 - AMBIENT_K4) / T4_REF_RANGE_K4;
  let cool = radiateCoefW * factor * dt;
  const maxCool = (tempK - AMBIENT_K) * heatCapJperK;
  if (cool > maxCool) cool = maxCool;
  return tempK - cool / heatCapJperK;
}

const HEAT_FRACTION = 0.5;
const COLLISION_FREEBIE_J = 1_500;
const HEAT_SHARE_JITTER = 0.15;

export function collisionHeatJ(eDiss: number): number {
  return eDiss > COLLISION_FREEBIE_J
    ? (eDiss - COLLISION_FREEBIE_J) * HEAT_FRACTION
    : 0;
}

export function heatShareJ(totalHeatJ: number, participants: number): number {
  if (totalHeatJ <= 0 || participants <= 0) return 0;
  const even = totalHeatJ / participants;
  return even * (1 + (Math.random() * 2 - 1) * HEAT_SHARE_JITTER);
}

const THERMAL_CONDUCTANCE_W_PER_K = 300_000;

/** m·c, floored at 1 for hollow modules. */
export function thermalCap(def: ModuleDef): number {
  return Math.max(1, def.stats.mass) * def.stats.thermalInertia;
}

const _edgesBySource = new Map<ShipModule, HeatPumpEdge[]>();
const _edgeListPool: HeatPumpEdge[][] = [];

/** Caller handles melt-point damage to avoid thermal→tick cycle. */
export function stepShipThermal(
  ship: Ship,
  dt: number,
  activeHeatByModule: Float64Array,
  heatPumpEdges: readonly HeatPumpEdge[],
  conductionEdges: readonly ConductionEdge[],
): void {
  for (let mi = 0; mi < ship.modules.length; mi++) {
    const m = ship.modules[mi];
    if (m.hp <= 0) continue;
    const def = m.def;
    let heatGenW = activeHeatByModule[mi];
    if (!m.disabled && m.manned !== false && !m.fuelStarved
        && def.category === "reactor" && def.stats.powerOutW > 0) {
      heatGenW += moduleActiveWasteHeatW(def) * (m.loadFactor ?? 0);
    }
    if (heatGenW <= 0) continue;
    m.tempK += (heatGenW * dt) / thermalCap(def);
  }

  // Conduction over the precomputed adjacency graph (constants cached in the
  // network cache, rebuilt only on structural change). Same per-edge formula,
  // same edge order as the old O(N²) scan → bit-identical temperatures. The
  // hp guard is retained so a module killed since the last rebuild is skipped
  // exactly as before.
  for (let i = 0; i < conductionEdges.length; i++) {
    const e = conductionEdges[i];
    const a = e.a;
    const b = e.b;
    if (a.hp <= 0 || b.hp <= 0) continue;
    const dT = a.tempK - b.tempK;
    if (Math.abs(dT) < 0.001) continue;
    const Q = THERMAL_CONDUCTANCE_W_PER_K * e.condFactor * e.sharedCells * dT * dt;
    a.tempK -= Q / e.capA;
    b.tempK += Q / e.capB;
  }

  _edgesBySource.clear();
  for (const edge of heatPumpEdges) {
    if (edge.from.hp <= 0 || edge.to.hp <= 0) continue;
    let list = _edgesBySource.get(edge.from);
    if (!list) {
      list = _edgeListPool.pop() ?? [];
      list.length = 0;
      _edgesBySource.set(edge.from, list);
    }
    list.push(edge);
  }
  for (const [source, edges] of _edgesBySource) {
    const capA = thermalCap(source.def);
    const maxFromSource = Math.max(0, (source.tempK - AMBIENT_K) * capA);
    let totalWanted = 0;
    for (const edge of edges) totalWanted += edge.wattsOut * dt;
    if (totalWanted > 0) {
      const scale = totalWanted <= maxFromSource ? 1 : maxFromSource / totalWanted;
      let drained = 0;
      for (const edge of edges) {
        const Q = edge.wattsOut * dt * scale;
        if (Q <= 0) continue;
        edge.to.tempK += Q / thermalCap(edge.to.def);
        drained += Q;
      }
      source.tempK -= drained / capA;
    }
  }
  for (const list of _edgesBySource.values()) _edgeListPool.push(list);

  for (const m of ship.modules) {
    if (m.hp <= 0) continue;
    if (m.tempK > AMBIENT_K) {
      const def = m.def;
      m.tempK = radiativeCool(m.tempK, thermalCap(def), moduleRadiateCoefW(def), dt);
    }
    if (m.tempK < AMBIENT_K) m.tempK = AMBIENT_K;
  }
}
